/**
 * المرحلة 1.35 — the child file, as the centre's paper file reads (photos 2–5):
 *
 *   الغلاف        the booklet's inner cover: the child, the stay, who is in charge
 *   سجل الدخول    «سجل دخول طفل» and the admission consent, printable, with the
 *                 birth block and the guardian's to correct
 *   منحنى النمو   the blue sheet: the CDC chart for the child's sex, the
 *                 readings joined with the last one's percentile, the table, «+ قياس»
 *   الملاحظات     the lined notes page, date and note
 *
 * Opened from the reception's patient tabs and the doctor's dashboard under the
 * `childFile` policy (domain/childFile.js seesChildFile). Every sheet is paper
 * whatever the theme, so the screen and the print are the same page.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, DateInput, ErrorState, Field, Icon, Input, Modal, Select, Spinner, Tabs, Textarea, TimeInput } from '../../components/common';
import { childFileApi } from '../../api/endpoints';
import { useNotifications } from '../../context/NotificationContext';
import { usePermissions } from '../../context/PermissionContext';
import { useI18n } from '../../i18n/I18nProvider';
import { activeCountry, formatDate, formatDateTime, formatTime } from '../../domain/locale';
import { BIRTH_FIELDS, GUARDIAN_FIELDS, MEASURES, SHEETS, ageLabel, blankGrowth, growthPayload, growthReady, percentileTone, pointsOf, profileForm, profilePayload } from '../../domain/childFile';
import { centerOpen } from '../../domain/centerPolicy';
import { admits, dayLabel, stayDay } from '../../domain/stays';
import { StayBooklet } from '../nursing/StayBooklet';
import { AdmitForm, DischargeBox } from '../nursing/StayAdmission';
import { ConsentPrints } from './ConsentPrints';
import { GrowthChart } from './GrowthChart';
import { Line, PrintButton, SheetHead, weekday } from './Paper';

const SHEET_ICONS = {
  consents: 'file-text',
  cover: 'book-open',
  admission: 'clipboard-check',
  growth: 'trending-up',
  notes: 'notebook-pen',
};

/* ── the four sheets ──────────────────────────────────────────────────── */

/**
 * The stay on the cover (المرحلة 1.35, T7), under `stayBooklet`: the open one
 * with its booklet and its discharge, or «دخول إقامة» for those who admit.
 */
function StayBox({ data, onChange }) {
  const { t } = useI18n();
  const { can, locale } = usePermissions();
  const [admitting, setAdmitting] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [stayData, setStayData] = useState(null);
  const [bookletKey, setBookletKey] = useState(0);
  if (!centerOpen(locale, 'stayBooklet')) return null;
  const current = data.stays.find((s) => s.status === 'open') || null;
  const reload = () => childFileApi.get(data.patient.id).then(onChange).catch(() => {});
  return (
    <section className="cf-stay cf-noprint" data-testid="child-stay">
      <h4 className="cf-stay__title">
        <Icon name="bed-double" size={18} /> {t('stay.current.title')}
      </h4>
      {current ? (
        <div className="cf-stay__row">
          <p className="cf-stay__meta">
            <span className="sb-chip sb-chip--day">{dayLabel(stayDay(current.admittedAt, new Date(), activeCountry().timezone), t)}</span>
            {current.unitLabel && <span>{current.unitLabel}</span>}
            {current.attendingDoctorName && (
              <span>
                {t('stay.banner.doctor')}: {current.attendingDoctorName}
              </span>
            )}
            <span>{t('stay.banner.admitted', { date: formatDateTime(current.admittedAt) })}</span>
          </p>
          <Button icon="book-open" onClick={() => setOpenId(current.id)} data-testid="child-stay-open">
            {t('stay.current.open')}
          </Button>
        </div>
      ) : admitting ? (
        <AdmitForm
          patient={data.patient}
          onCancel={() => setAdmitting(false)}
          onDone={(fresh) => {
            setAdmitting(false);
            reload();
            setOpenId(fresh.stay.id);
          }}
        />
      ) : (
        <div className="cf-stay__row">
          <p className="cf-stay__meta">{t('stay.current.none')}</p>
          {admits(can) && (
            <Button icon="bed-double" onClick={() => setAdmitting(true)} data-testid="child-stay-admit">
              {t('stay.admit.title')}
            </Button>
          )}
        </div>
      )}
      {openId && (
        <Modal
          open
          onClose={() => {
            setOpenId(null);
            setStayData(null);
            reload();
          }}
          title={data.patient.fullName}
          icon="bed-double"
          size="xl"
        >
          <StayBooklet key={bookletKey} stayId={openId} onChanged={setStayData} />
          {stayData && (
            <DischargeBox
              data={stayData}
              onChange={(fresh) => {
                setStayData(fresh);
                setBookletKey((k) => k + 1);
              }}
            />
          )}
        </Modal>
      )}
    </section>
  );
}

function CoverSheet({ data, onChange }) {
  const { t, lang } = useI18n();
  const ref = useRef(null);
  const { patient, profile } = data;
  const stay = data.stays[0] || null;
  return (
    <div className="space-y-3">
      <StayBox data={data} onChange={onChange} />
      <div className="flex justify-end">
        <PrintButton target={ref} />
      </div>
      <article ref={ref} className="cf-paper cf-paper--cover" data-testid="child-cover">
        <SheetHead title={t('childFile.cover.title')} subtitle={t('childFile.cover.subtitle')} />
        <div className="cf-lines">
          <Line label={t('childFile.cover.childName')} wide>
            {patient.fullName}
          </Line>
          <Line label={t('childFile.cover.sex')}>{patient.gender ? t(`childFile.sex.${patient.gender}`) : ''}</Line>
          <Line label={t('childFile.cover.age')}>{ageLabel(patient.dateOfBirth, t)}</Line>
          <Line label={t('childFile.cover.bookedAt')}>{stay?.bookedAt ? formatDate(stay.bookedAt) : ''}</Line>
          <Line label={t('childFile.cover.bookedTime')}>{stay?.bookedAt ? formatTime(stay.bookedAt) : ''}</Line>
          <Line label={t('childFile.cover.dischargedAt')}>{stay?.dischargedAt ? formatDate(stay.dischargedAt) : ''}</Line>
          <Line label={t('childFile.cover.dischargedTime')}>{stay?.dischargedAt ? formatTime(stay.dischargedAt) : ''}</Line>
          <Line label={t('childFile.cover.phone')}>{(profile.guardianPhone || patient.phonePrimary) && <span dir="ltr">{profile.guardianPhone || patient.phonePrimary}</span>}</Line>
          <Line label={t('childFile.cover.responsible')}>{stay?.responsibleName || ''}</Line>
          <Line label={t('childFile.cover.doctor')}>{stay?.attendingDoctorName || ''}</Line>
        </div>
        <p className="cf-paper__foot">
          {t('childFile.cover.opened', { date: formatDate(patient.openedAt) })} · {weekday(patient.openedAt, lang)}
        </p>
      </article>
    </div>
  );
}

function ProfileEditor({ data, onSaved }) {
  const { t } = useI18n();
  const { success, error: toastError } = useNotifications();
  const [form, setForm] = useState(() => profileForm(data.profile));
  const [saving, setSaving] = useState(false);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const save = async () => {
    setSaving(true);
    try {
      const fresh = await childFileApi.save(data.patient.id, profilePayload(form));
      success(t('childFile.profile.saved'));
      onSaved(fresh);
    } catch (err) {
      toastError(err.message || t('childFile.profile.failed'));
    } finally {
      setSaving(false);
    }
  };
  return (
    <section className="cf-editor" data-testid="child-profile-editor">
      <h4 className="cf-editor__title">
        <Icon name="baby" size={18} /> {t('childFile.profile.birthTitle')}
      </h4>
      <div className="cf-editor__grid">
        {BIRTH_FIELDS.map((f) => (
          <Field key={f.key} label={t(`childFile.profile.${f.key}`)}>
            {f.kind === 'choice' ? (
              <Select value={form[f.key]} onChange={set(f.key)} data-testid={`child-${f.key}`}>
                <option value="">—</option>
                {f.choices.map((c) => (
                  <option key={c} value={c}>
                    {t(`childFile.profile.${f.key}_${c}`)}
                  </option>
                ))}
              </Select>
            ) : f.kind === 'time' ? (
              <TimeInput value={form[f.key]} onChange={set(f.key)} data-testid={`child-${f.key}`} />
            ) : (
              <Input inputMode={f.kind === 'number' ? 'decimal' : undefined} dir={f.kind === 'text' ? undefined : 'ltr'} value={form[f.key]} onChange={set(f.key)} data-testid={`child-${f.key}`} />
            )}
          </Field>
        ))}
      </div>
      <h4 className="cf-editor__title">
        <Icon name="users" size={18} /> {t('childFile.profile.guardianTitle')}
      </h4>
      <div className="cf-editor__grid">
        {GUARDIAN_FIELDS.map((key) => (
          <Field key={key} label={t(`childFile.profile.${key}`)}>
            {key === 'guardianRelation' ? (
              <Select value={form[key]} onChange={set(key)}>
                <option value="">—</option>
                {['father', 'mother', 'guardian', 'other'].map((r) => (
                  <option key={r} value={r}>
                    {t(`patientFile.rel.${r}`)}
                  </option>
                ))}
              </Select>
            ) : (
              <Input
                value={form[key]}
                onChange={set(key)}
                dir={['guardianPhone', 'guardianNationalId'].includes(key) ? 'ltr' : undefined}
                inputMode={['guardianPhone', 'guardianNationalId'].includes(key) ? 'numeric' : undefined}
              />
            )}
          </Field>
        ))}
      </div>
      <div className="flex justify-end">
        <Button icon="save" loading={saving} onClick={save} data-testid="child-profile-save">
          {t('childFile.profile.save')}
        </Button>
      </div>
    </section>
  );
}

function AdmissionSheet({ data, onChange }) {
  const { t, lang } = useI18n();
  const { institutionName } = usePermissions();
  const ref = useRef(null);
  const [editing, setEditing] = useState(false);
  const { patient, profile } = data;
  const stay = data.stays[0] || null;
  const centre = institutionName || t('childFile.admission.theCentre');
  const relation = profile.guardianRelation ? t(`patientFile.rel.${profile.guardianRelation}`) : '';
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap justify-end gap-2">
        {data.can.editProfile && (
          <Button variant={editing ? 'subtle' : 'ghost'} icon="pencil" onClick={() => setEditing((v) => !v)} data-testid="child-edit-profile">
            {t('childFile.profile.edit')}
          </Button>
        )}
        <PrintButton target={ref} />
      </div>
      {editing && (
        <ProfileEditor
          data={data}
          onSaved={(fresh) => {
            setEditing(false);
            onChange(fresh);
          }}
        />
      )}
      <article ref={ref} className="cf-paper" data-testid="child-admission">
        <SheetHead title={t('childFile.admission.title')} />
        <div className="cf-lines cf-lines--two">
          <Line label={t('childFile.admission.childName')} wide>
            {patient.fullName}
          </Line>
          <Line label={t('childFile.admission.sex')}>{patient.gender ? t(`childFile.sex.${patient.gender}`) : ''}</Line>
          <Line label={t('childFile.admission.birthDate')}>
            {patient.dateOfBirth ? `${formatDate(`${patient.dateOfBirth}T12:00:00Z`)} · ${weekday(`${patient.dateOfBirth}T12:00:00Z`, lang)}` : ''}
          </Line>
          <Line label={t('childFile.admission.birthTime')}>{profile.birthTime ? <span dir="ltr">{profile.birthTime}</span> : ''}</Line>
          <Line label={t('childFile.admission.birthPlace')}>{profile.birthPlace}</Line>
          <Line label={t('childFile.admission.birthDoctor')}>{profile.birthDoctor}</Line>
          <Line label={t('childFile.admission.admittedAt')}>{stay?.admittedAt ? `${formatDateTime(stay.admittedAt)} · ${weekday(stay.admittedAt, lang)}` : ''}</Line>
          <Line label={t('childFile.admission.motherName')}>{profile.motherName}</Line>
          <Line label={t('childFile.admission.nationalId')}>{(profile.guardianNationalId || patient.nationalId) && <span dir="ltr">{profile.guardianNationalId || patient.nationalId}</span>}</Line>
          <Line label={t('childFile.admission.address')} wide>
            {profile.address}
          </Line>
          <Line label={t('childFile.admission.phone')}>{(profile.guardianPhone || patient.phonePrimary) && <span dir="ltr">{profile.guardianPhone || patient.phonePrimary}</span>}</Line>
          <Line label={t('childFile.admission.dischargedAt')}>{stay?.dischargedAt ? `${formatDateTime(stay.dischargedAt)} · ${weekday(stay.dischargedAt, lang)}` : ''}</Line>
        </div>

        <section className="cf-consent" data-testid="child-consent">
          <h4 className="cf-consent__title">{t('childFile.admission.consentTitle', { centre })}</h4>
          <p className="cf-consent__text">
            {t('childFile.admission.consentLead')} <b>{profile.guardianName || '..................................'}</b> {t('childFile.admission.consentChild')} <b>{patient.fullName}</b>{' '}
            {t('childFile.admission.consentRelation')} <b>{relation || '............'}</b> {t('childFile.admission.consentAddress')} <b>{profile.address || '..................................'}</b>{' '}
            {t('childFile.admission.consentBody', { centre })}
          </p>
          <div className="cf-sign">
            <Line label={t('childFile.admission.guardianSignature')} />
            <Line label={t('childFile.admission.nationalId')}>{profile.guardianNationalId && <span dir="ltr">{profile.guardianNationalId}</span>}</Line>
            <Line label={t('childFile.admission.date')} />
          </div>
        </section>

        <section className="cf-clearance">
          <p className="cf-clearance__title">{t('childFile.admission.clearanceTitle')}</p>
          <p>{t('childFile.admission.clearanceText')}</p>
          <Line label={t('childFile.admission.accountantSignature')} />
        </section>
      </article>
    </div>
  );
}

function PercentileCard({ measure, reading }) {
  const { t } = useI18n();
  const tone = percentileTone(reading?.percentile);
  return (
    <div className={`cf-pcard cf-pcard--${tone}`} data-testid={`child-percentile-${measure.key}`}>
      <span className="cf-pcard__label">{t(`childFile.measure.${measure.key}`)}</span>
      <span className="cf-pcard__value">{reading ? `${reading.value} ${t(`childFile.unit.${measure.unit}`)}` : '—'}</span>
      <span className="cf-pcard__p">
        {reading && reading.percentile !== null
          ? t('childFile.chart.percentileShort', {
              p: Math.round(reading.percentile),
            })
          : t('childFile.chart.offChart')}
      </span>
      {reading && reading.percentile !== null && <span className="cf-pcard__hint">{t(`childFile.tone.${tone}`)}</span>}
    </div>
  );
}

function GrowthForm({ patientId, onAdded }) {
  const { t } = useI18n();
  const { success, error: toastError } = useNotifications();
  const [form, setForm] = useState(() => blankGrowth());
  const [saving, setSaving] = useState(false);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const add = async () => {
    setSaving(true);
    try {
      const fresh = await childFileApi.addGrowth(patientId, growthPayload(form));
      success(t('childFile.growth.added'));
      setForm(blankGrowth());
      onAdded(fresh);
    } catch (err) {
      toastError(err.message || t('childFile.growth.failed'));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="cf-gform" data-testid="growth-form">
      <Field label={t('childFile.growth.date')}>
        <DateInput value={form.at} onChange={set('at')} max={blankGrowth().at} clearable={false} data-testid="growth-at" />
      </Field>
      {MEASURES.map((m) => (
        <Field key={m.key} label={`${t(`childFile.measure.${m.key}`)} (${t(`childFile.unit.${m.unit}`)})`}>
          <Input inputMode="decimal" dir="ltr" value={form[m.field]} onChange={set(m.field)} data-testid={`growth-${m.key}`} />
        </Field>
      ))}
      <Field label={t('childFile.growth.comment')} className="cf-gform__comment">
        <Input value={form.comment} onChange={set('comment')} data-testid="growth-comment" />
      </Field>
      <Button icon="plus" loading={saving} disabled={!growthReady(form)} onClick={add} data-testid="growth-add">
        {t('childFile.growth.add')}
      </Button>
    </div>
  );
}

function GrowthSheet({ data, onChange }) {
  const { t } = useI18n();
  const { success, error: toastError } = useNotifications();
  const ref = useRef(null);
  const { patient, profile, growth } = data;
  const remove = async (row) => {
    try {
      onChange(await childFileApi.removeGrowth(patient.id, row.at));
      success(t('childFile.growth.removed'));
    } catch (err) {
      toastError(err.message);
    }
  };
  const charts = useMemo(() => MEASURES.map((m) => ({ ...m, points: pointsOf(growth, m.key) })), [growth]);
  return (
    <div className="space-y-3">
      <div className="cf-pcards">
        {MEASURES.map((m) => (
          <PercentileCard key={m.key} measure={m} reading={data.percentiles[m.key]} />
        ))}
      </div>
      {data.can.measure && <GrowthForm patientId={patient.id} onAdded={onChange} />}
      <div className="flex justify-end">
        <PrintButton target={ref} />
      </div>
      <article ref={ref} className={`cf-paper cf-paper--growth cf-paper--${patient.gender === 'female' ? 'girl' : 'boy'}`} data-testid="child-growth">
        <SheetHead title={t('childFile.growth.title')} subtitle={t('childFile.growth.subtitle')} />
        <div className="cf-lines cf-lines--four">
          <Line label={t('childFile.growth.name')} wide>
            {patient.fullName}
          </Line>
          <Line label={t('childFile.growth.age')}>{ageLabel(patient.dateOfBirth, t)}</Line>
          <Line label={t('childFile.growth.phone')}>{(profile.guardianPhone || patient.phonePrimary) && <span dir="ltr">{profile.guardianPhone || patient.phonePrimary}</span>}</Line>
          <Line label={t('childFile.growth.address')}>{profile.address}</Line>
          <Line label={t('childFile.growth.feeding')}>{profile.feedingType ? t(`childFile.profile.feedingType_${profile.feedingType}`) : ''}</Line>
          <Line label={t('childFile.growth.delivery')}>{profile.deliveryType ? t(`childFile.profile.deliveryType_${profile.deliveryType}`) : ''}</Line>
          <Line label={t('childFile.growth.gestation')}>{profile.gestationWeeks ? t('childFile.growth.weeks', { n: profile.gestationWeeks }) : ''}</Line>
        </div>
        {!patient.gender && <p className="cf-paper__warn">{t('childFile.growth.noSex')}</p>}
        {!patient.dateOfBirth && <p className="cf-paper__warn">{t('childFile.growth.noBirth')}</p>}
        <div className="cf-charts">
          {charts.map((c) => (
            <GrowthChart key={c.key} chart={c.chart} sex={patient.gender} points={c.points} />
          ))}
        </div>
        <table className="cf-table" data-testid="growth-table">
          <thead>
            <tr>
              <th>{t('childFile.growth.motherStature')}</th>
              <th>{t('childFile.growth.fatherStature')}</th>
              <th>{t('childFile.growth.gestationalAge')}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td dir="ltr">{profile.motherHeightCm ? `${profile.motherHeightCm} cm` : ''}</td>
              <td dir="ltr">{profile.fatherHeightCm ? `${profile.fatherHeightCm} cm` : ''}</td>
              <td>{profile.gestationWeeks ? t('childFile.growth.weeks', { n: profile.gestationWeeks }) : ''}</td>
            </tr>
          </tbody>
        </table>
        <div className="cf-scroll">
          <table className="cf-table cf-table--rows">
            <thead>
              <tr>
                <th>{t('childFile.growth.date')}</th>
                <th>{t('childFile.growth.age')}</th>
                <th>{t('childFile.measure.weight')}</th>
                <th>{t('childFile.measure.length')}</th>
                <th>{t('childFile.measure.head')}</th>
                <th>{t('childFile.growth.comment')}</th>
                <th className="cf-noprint">{t('childFile.growth.by')}</th>
                {data.can.measure && <th className="cf-noprint" aria-label={t('childFile.growth.remove')} />}
              </tr>
            </thead>
            <tbody>
              {growth.length === 0 && (
                <tr>
                  <td colSpan={8} className="cf-table__empty">
                    {t('childFile.growth.empty')}
                  </td>
                </tr>
              )}
              {growth.map((r) => (
                <tr key={r.at} data-testid="growth-row">
                  <td data-label={t('childFile.growth.date')}>{formatDate(r.at)}</td>
                  <td data-label={t('childFile.growth.age')}>{r.ageMonths !== null ? t('childFile.growth.monthsValue', { n: r.ageMonths }) : '—'}</td>
                  <td dir="ltr" data-label={t('childFile.measure.weight')}>{r.weight ?? ''}</td>
                  <td dir="ltr" data-label={t('childFile.measure.length')}>{r.length ?? ''}</td>
                  <td dir="ltr" data-label={t('childFile.measure.head')}>{r.head ?? ''}</td>
                  <td data-label={t('childFile.growth.comment')}>{r.comment || ''}</td>
                  <td className="cf-noprint" data-label={t('childFile.growth.by')}>{r.by || ''}</td>
                  {data.can.measure && (
                    <td className="cf-noprint">
                      <button type="button" className="cf-table__remove" onClick={() => remove(r)} title={t('childFile.growth.remove')} aria-label={t('childFile.growth.remove')}>
                        <Icon name="trash-2" size={15} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="cf-paper__foot">{t('childFile.growth.source')}</p>
      </article>
    </div>
  );
}

function NotesSheet({ data, onChange }) {
  const { t } = useI18n();
  const { success, error: toastError } = useNotifications();
  const ref = useRef(null);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const add = async () => {
    setSaving(true);
    try {
      await childFileApi.addNote(data.patient.id, text.trim());
      setText('');
      onChange(await childFileApi.get(data.patient.id));
      success(t('childFile.notes.added'));
    } catch (err) {
      toastError(err.message);
    } finally {
      setSaving(false);
    }
  };
  const notes = [...data.notes].reverse();
  return (
    <div className="space-y-3">
      {data.can.note && (
        <div className="cf-nform" data-testid="notes-form">
          <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder={t('childFile.notes.placeholder')} data-testid="notes-text" />
          <Button icon="plus" loading={saving} disabled={!text.trim()} onClick={add} data-testid="notes-add">
            {t('childFile.notes.add')}
          </Button>
        </div>
      )}
      <div className="flex justify-end">
        <PrintButton target={ref} />
      </div>
      <article ref={ref} className="cf-paper cf-paper--notes" data-testid="child-notes">
        <SheetHead title={t('childFile.notes.title')} subtitle={data.patient.fullName} />
        <table className="cf-ruled">
          <thead>
            <tr>
              <th>{t('childFile.notes.date')}</th>
              <th>{t('childFile.notes.note')}</th>
            </tr>
          </thead>
          <tbody>
            {notes.map((n) => (
              <tr key={n.id}>
                <td>{formatDate(n.at)}</td>
                <td>
                  {n.text}
                  {n.by && <span className="cf-ruled__by"> — {n.by}</span>}
                </td>
              </tr>
            ))}
            {Array.from({ length: Math.max(0, 8 - notes.length) }, (_, i) => (
              <tr key={`blank-${i}`} className="cf-ruled__blank">
                <td>{' '}</td>
                <td>{' '}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </article>
    </div>
  );
}

/* ── the file ─────────────────────────────────────────────────────────── */

/**
 * @param {{ patient: { id: number }, defaultSheet?: 'cover'|'admission'|'growth'|'notes' }} props
 */
export function ChildFile({ patient, defaultSheet = 'growth' }) {
  const { t } = useI18n();
  const { locale } = usePermissions();
  const booklet = centerOpen(locale, 'stayBooklet');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [sheet, setSheet] = useState(SHEETS.includes(defaultSheet) ? defaultSheet : 'growth');
  const load = () =>
    childFileApi
      .get(patient.id)
      .then((d) => {
        setData(d);
        setError(null);
      })
      .catch(setError);
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient.id]);

  if (error) return <ErrorState error={error} onRetry={load} />;
  if (!data) return <Spinner label={t('childFile.loading')} variant="inline" />;

  const p = data.patient;
  // The consents follow the stay booklet (1.35.9).
  const sheets = SHEETS.filter((id) => id !== 'consents' || booklet);
  const relation = data.profile.guardianRelation ? t(`patientFile.rel.${data.profile.guardianRelation}`) : t('childFile.guardian');
  return (
    <div className="cf" data-testid="child-file">
      <div className={`cf-banner cf-banner--${p.gender === 'female' ? 'girl' : 'boy'}`}>
        <span className="cf-banner__face" aria-hidden="true">
          <Icon name="baby" size={26} />
        </span>
        <div className="cf-banner__who">
          <p className="cf-banner__name">{p.fullName}</p>
          <p className="cf-banner__meta">
            {p.gender && <span>{t(`childFile.sex.${p.gender}`)}</span>}
            <span>{ageLabel(p.dateOfBirth, t)}</span>
            {data.profile.guardianName && (
              <span>
                {relation}: {data.profile.guardianName}
              </span>
            )}
            {(data.profile.guardianPhone || p.phonePrimary) && <span dir="ltr">{data.profile.guardianPhone || p.phonePrimary}</span>}
          </p>
        </div>
        <div className="cf-banner__ps">
          {MEASURES.map((m) => {
            const r = data.percentiles[m.key];
            if (!r || r.percentile === null) return null;
            return (
              <span key={m.key} className={`cf-chip cf-chip--${percentileTone(r.percentile)}`}>
                {t(`childFile.measure.${m.key}`)} ·{' '}
                {t('childFile.chart.percentileShort', {
                  p: Math.round(r.percentile),
                })}
              </span>
            );
          })}
        </div>
      </div>
      <Tabs
        tabs={sheets.map((id) => ({
          id,
          label: t(`childFile.sheet.${id}`),
          icon: SHEET_ICONS[id],
          count: id === 'growth' ? data.growth.length : id === 'notes' ? data.notes.length : undefined,
        }))}
        active={sheet}
        onChange={setSheet}
      />
      {sheet === 'cover' && <CoverSheet data={data} onChange={setData} />}
      {sheet === 'admission' && <AdmissionSheet data={data} onChange={setData} />}
      {sheet === 'growth' && <GrowthSheet data={data} onChange={setData} />}
      {sheet === 'notes' && <NotesSheet data={data} onChange={setData} />}
      {sheet === 'consents' && booklet && <ConsentPrints patient={data.patient} profile={data.profile} stay={data.stays[0] || null} />}
    </div>
  );
}

export default ChildFile;
